import React, {ReactNode, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {toast} from 'react-toastify'
import {useAuth} from '../../../modules/auth'
import {shouldDisplayFonctionnalite} from '../../../../components/helpers'
import {SavDetailResponse} from '../../../../components/sav/Models'
import {convertirSavClient, getSavDossier, savErreur} from '../../../../components/sav/requests'
import {
  formatSavDate,
  nomSavClient,
  nomSavUtilisateur,
  SAV_ORIGINES,
} from '../../../../components/sav/libelles'
import SavStatutBadge from './SavStatutBadge'
import SavSourceIcone from './SavSourceIcone'
import SavStepper from './SavStepper'
import SavAc10 from './SavAc10'
import SavReassignerModal from './SavReassignerModal'
import SavClientCreateModal from './SavClientCreateModal'

type Props = {
  detail: SavDetailResponse
  onRecharger: (detail?: SavDetailResponse) => void
  ac10: {etape: string; acteur: string; delai: string}
  // Bouton d'action contextuel, à droite de la carte client
  action?: ReactNode
}

const tempsEcoule = (depuis: string, jusqua?: string | null): string => {
  const minutes = Math.max(
    0,
    Math.floor(
      ((jusqua ? new Date(jusqua) : new Date()).getTime() - new Date(depuis).getTime()) / 60000
    )
  )
  const jours = Math.floor(minutes / 1440)
  const heures = Math.floor((minutes % 1440) / 60)
  return jours ? `${jours} j ${heures} h` : `${heures} h ${minutes % 60} min`
}

// En-tête commun des écrans de dossier (maquette 0.5)
const SavDossierHeader = ({detail, onRecharger, ac10, action}: Props) => {
  const navigate = useNavigate()
  const {currentUser} = useAuth()
  const [reassigner, setReassigner] = useState(false)
  const [conversion, setConversion] = useState(false)
  const [plusInfos, setPlusInfos] = useState(false)
  const {dossier} = detail
  const client = dossier.client
  const generique = !!client?.generique

  const recharger = () => getSavDossier(dossier.id).then(onRecharger)

  const convertir = (clientId: number) => {
    convertirSavClient(dossier.id, clientId)
      .then((reponse) => {
        toast.success(
          `Fiche créée : ${reponse.dossiers.length} dossier(s) SAV rattaché(s) (${reponse.dossiers
            .map((d) => d.numero)
            .join(', ')}).`
        )
        setConversion(false)
        recharger()
      })
      .catch((e) => toast.error(savErreur(e)))
  }

  const infos: Array<[string, ReactNode]> = [
    ['Source', dossier.source?.libelle],
    ['Mode de contact', dossier.modeContact === 'MAIL' ? 'Mail' : 'Téléphone'],
    ['Centre de la vente', dossier.centre?.nom],
    ["Vente d'origine", SAV_ORIGINES[dossier.origine]],
    [
      "Date d'achat",
      dossier.dateAchat
        ? `${formatSavDate(dossier.dateAchat)} (${
            dossier.dateAchatVerifiee ? 'vérifiée' : 'déclarée'
          })`
        : '—',
    ],
    ['N° appelant', dossier.appelNumero],
    ['Interlocuteur', dossier.interlocuteur],
    ["Date de l'appel", formatSavDate(dossier.dateAppel, true)],
    ['Boîte mail', dossier.mailBoite],
    ['Expéditeur', dossier.mailExpediteur],
    ['Objet du mail', dossier.mailObjet],
    ['Comptoir', dossier.siteComptoir?.nom],
    ['Plateforme', dossier.plateforme],
    ['Message plateforme', dossier.messagePlateforme],
    ['Commentaire', dossier.commentaireSource],
    ['Saisi par', nomSavUtilisateur(dossier.utilisateurSaisie)],
  ]

  return (
    <>
      <div className='d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4'>
        <div className='d-flex align-items-center gap-4'>
          <button className='btn btn-sm btn-light' onClick={() => navigate('/sav/liste')}>
            <i className='bi bi-chevron-left'></i> Retour
          </button>
          <SavAc10 {...ac10} />
        </div>
        <SavStepper
          statut={dossier.statut.code}
          echange={dossier.solution?.comportement === 'ECHANGE'}
        />
        <div className='d-flex align-items-center gap-3'>
          <span className='fw-bold text-gray-700'>{dossier.centre?.nom}</span>
          <i className='bi bi-person fs-4'></i>
          <span>{nomSavUtilisateur(dossier.utilisateurAssigne) || 'Non assigné'}</span>
          {shouldDisplayFonctionnalite(currentUser, 'SAV_QUALIFIER') && !dossier.statut.final && (
            <button
              className='btn btn-sm btn-light-warning border border-warning'
              onClick={() => setReassigner(true)}
            >
              Réassigner
            </button>
          )}
        </div>
      </div>

      <div className='card mb-5'>
        <div className='card-body'>
          <div className='row g-5'>
            <div className='col-lg-4'>
              <div className='fs-3 fw-bolder text-uppercase'>
                {nomSavClient(client, dossier.contactNom)}
              </div>
              <div className='d-flex flex-wrap gap-2 my-2'>
                {generique ? (
                  <span className='badge badge-light-warning'>Client générique</span>
                ) : (
                  <>
                    <span className='badge badge-light-success'>CLIENT</span>
                    <span className='badge badge-light-primary'>
                      {client?.professionnel ? 'Professionnel' : 'Particulier'}
                    </span>
                  </>
                )}
              </div>
              {generique && (
                <div className='text-muted fs-7 mb-1'>
                  Saisi sur le client {nomSavClient(client)}
                </div>
              )}
              {(generique ? dossier.contactEmail : client?.email) && (
                <div>
                  <i className='bi bi-envelope me-2'></i>
                  {generique ? dossier.contactEmail : client?.email}
                </div>
              )}
              {(generique ? dossier.contactTelephone : client?.telephone || client?.mobile) && (
                <div>
                  <i className='bi bi-telephone me-2'></i>
                  {generique ? dossier.contactTelephone : client?.telephone || client?.mobile}
                </div>
              )}
              {generique && shouldDisplayFonctionnalite(currentUser, 'SAV_QUALIFIER') && (
                <button
                  className='btn btn-link p-0 mt-2 fw-bold text-success'
                  onClick={() => setConversion(true)}
                >
                  Créer la fiche client
                </button>
              )}
            </div>
            <div className='col-lg-5'>
              <div className='text-muted fw-bold mb-1'>Réclamation :</div>
              <div className='bg-light rounded p-4' style={{whiteSpace: 'pre-line'}}>
                {dossier.description}
              </div>
            </div>
            <div className='col-lg-3 d-flex flex-column align-items-lg-end gap-2'>
              <div className='fs-4 fw-bold'>
                SAV <span className='text-success'>#{dossier.numero}</span>
              </div>
              <div className='d-flex align-items-center gap-2'>
                <SavSourceIcone source={dossier.source} />
                <SavStatutBadge code={dossier.statut.code} />
              </div>
              <div className='text-muted'>
                <i className='bi bi-hourglass-split text-danger me-1'></i>
                Temps écoulé : {tempsEcoule(dossier.dateCreation, dossier.dateCloture)}
              </div>
              {dossier.dateEcheance && (
                <div className={detail.enRetard ? 'text-danger fw-bolder' : 'text-muted'}>
                  Échéance : {formatSavDate(dossier.dateEcheance, true)}
                  {detail.enRetard && ' (en retard)'}
                </div>
              )}
              {action}
            </div>
          </div>
          {plusInfos && (
            <div className='row g-3 mt-4 border-top pt-4'>
              {infos
                .filter(([, valeur]) => valeur)
                .map(([libelle, valeur]) => (
                  <div className='col-md-3' key={libelle}>
                    <div className='text-muted fs-7'>{libelle}</div>
                    <div className='fw-semibold'>{valeur}</div>
                  </div>
                ))}
            </div>
          )}
          <div className='text-center mt-3'>
            <button
              className='btn btn-link text-success fw-bold'
              onClick={() => setPlusInfos(!plusInfos)}
            >
              {plusInfos ? "Moins d'informations" : "Plus d'informations"}{' '}
              <i className={`bi ${plusInfos ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {reassigner && (
        <SavReassignerModal
          dossierId={dossier.id}
          assigne={dossier.utilisateurAssigne}
          onClose={() => setReassigner(false)}
          onSaved={() => {
            setReassigner(false)
            recharger()
          }}
        />
      )}
      {conversion && (
        <SavClientCreateModal
          titre='Créer la fiche client'
          prerempli={{
            professionnel: false,
            nom: dossier.contactNom ?? '',
            email: dossier.contactEmail ?? '',
            telephone: dossier.contactTelephone ?? '',
          }}
          onClose={() => setConversion(false)}
          onCree={convertir}
        />
      )}
    </>
  )
}

export default SavDossierHeader
